<template>
  <!--
    DECLARAÇÃO DE ENDEREÇO
    Documento de 1 página (A4) que atesta que o residente mora na ILPI.
    Segue o mesmo padrão de carregamento do PrintResident (rota /:id + endpoint de print).
  -->
  <div class="address-declaration">
    <b-overlay
      :show="loading"
      opacity="0.99"
      class="address-declaration__overlay"
    >
      <ErrorsMessages :errors="errors" />

      <div v-show="errors.length == 0" class="address-declaration__container">
        <!-- ===================== AÇÕES (somente tela) ===================== -->
        <div class="address-declaration__actions no-print">
          <!--
            Prevenção de erro: avisa ANTES de imprimir se algum dado obrigatório
            está vazio. Um documento legal com "--" no CNPJ ou CPF não tem validade.
          -->
          <div
            v-if="resident && missingFields.length"
            class="address-declaration__alert"
            role="alert"
          >
            <i
              class="fas fa-exclamation-triangle address-declaration__alert-icon"
              aria-hidden="true"
            ></i>
            <div>
              <strong>Dados incompletos.</strong>
              Os campos abaixo estão vazios e vão sair como "--" no documento.
              Complete o cadastro antes de imprimir:
              <ul class="address-declaration__alert-list">
                <li v-for="field in missingFields" :key="field">
                  {{ field }}
                </li>
              </ul>
            </div>
          </div>

          <v-btn
            class="address-declaration__btn-print"
            @click="printOut"
            aria-label="Imprimir declaração de endereço"
          >
            <i class="fas fa-print mr-2" aria-hidden="true"></i>
            IMPRIMIR DECLARAÇÃO
          </v-btn>
        </div>

        <!-- ===================== DOCUMENTO (folha A4) ===================== -->
        <article
          v-if="resident"
          class="address-declaration__page"
          aria-labelledby="address-declaration-title"
        >
          <!-- Cabeçalho: logo centralizada -->
          <header class="address-declaration__header">
            <img
              v-if="restHomeLogoUrl"
              :src="restHomeLogoUrl"
              :alt="'Logo da instituição ' + restHomeName"
              class="address-declaration__logo"
            />
            <!-- Fallback: sem logo, mostra o nome para o documento não ficar sem identificação -->
            <p v-else class="address-declaration__logo-fallback">
              {{ restHomeName }}
            </p>
          </header>

          <!-- Título -->
          <h1 id="address-declaration-title" class="address-declaration__title">
            DECLARAÇÃO DE ENDEREÇO
          </h1>

          <!-- Parágrafo de abertura -->
          <p class="address-declaration__text">
            Eu, <strong>{{ restHomeName }}</strong
            >, inscrita no CNPJ sob o nº <strong>{{ restHomeCnpj }}</strong
            >, com sede à <strong>{{ restHomeFullAddress }}</strong
            >, declaro, para os devidos fins, que o residente abaixo relacionado
            <strong>reside no endereço desta instituição</strong>:
          </p>

          <!-- Dados do residente (tabela semântica) -->
          <table class="address-declaration__table">
            <caption class="address-declaration__sr-only">
              Dados do residente declarado
            </caption>
            <thead>
              <tr>
                <th scope="col">Nome do Residente</th>
                <th scope="col">RG nº</th>
                <th scope="col">CPF nº</th>
                <th scope="col">Data de Nascimento</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ resident.name || "--" }}</td>
                <td class="address-declaration__nowrap">
                  {{ resident.rg || "--" }}
                </td>
                <td class="address-declaration__nowrap">
                  {{ resident.cpf || "--" }}
                </td>
                <td class="address-declaration__nowrap">
                  {{
                    resident.date_of_birth
                      ? formatDate(resident.date_of_birth, false)
                      : "--"
                  }}
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Parágrafo de fechamento -->
          <p class="address-declaration__text">
            Declaro ainda que as informações prestadas são verdadeiras e podem
            ser confirmadas a qualquer tempo,
            <strong>sob as penas da lei</strong>.
          </p>

          <!-- Local e data (data de hoje, por extenso) -->
          <p class="address-declaration__date">
            <strong>{{ placeAndDate }}</strong>
          </p>

          <!--
            Bloco de assinatura (não aparece na imagem de referência).
            Mantido porque uma declaração sem assinatura costuma ser recusada.
            Para remover, defina showSignature: false no data().
          -->
          <div v-if="showSignature" class="address-declaration__signature">
            <span
              class="address-declaration__signature-line"
              aria-hidden="true"
            ></span>
            <p class="address-declaration__signature-name">
              {{ restHomeName }}
            </p>
            <p class="address-declaration__signature-role">Responsável legal</p>
          </div>
        </article>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import ErrorsMessages from "../Components/ErrorsMessages";
import api from "../../services/api";
import { formatDate } from "../../lib/format";
import routes from "../../routes";
import { userData } from "../../lib/functions";

// Meses por extenso: evita depender do locale pt-br do moment estar carregado.
const MONTHS_PT = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

export default {
  name: "PrintAddressDeclaration",

  components: {
    ErrorsMessages,
  },

  data() {
    return {
      id: "",
      errors: [],
      loading: true,
      resident: null,

      // Dados da instituição vindos do userData().user (preenchidos no retrieve)
      restHomeName: "",
      restHomeLogoUrl: null,
      restHome: {},

      // Liga/desliga o bloco de assinatura
      showSignature: true,
    };
  },

  computed: {
    /** CNPJ formatado (00.000.000/0000-00) */
    restHomeCnpj() {
      const raw = this.restHome.cnpj;
      if (!raw) return "--";
      const digits = String(raw).replace(/\D/g, "");
      if (digits.length !== 14) return raw; // já formatado ou fora do padrão
      return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
      );
    },

    /**
     * CEP formatado (00000-000).
     * ATENÇÃO: o userData().user NÃO retorna CEP hoje.
     * Se o back-end incluir "rest_home_cep", ele aparece automaticamente.
     */
    restHomeCep() {
      const raw = this.restHome.cep;
      if (!raw) return "";
      const digits = String(raw).replace(/\D/g, "");
      return digits.length === 8
        ? digits.replace(/^(\d{5})(\d{3})$/, "$1-$2")
        : raw;
    },

    /**
     * Endereço no formato da imagem:
     * "Av. Belo Horizonte, nº 65 – Jd. São Caetano/SP – CEP: 09581-520"
     * Partes vazias são omitidas para não gerar "– –" no texto.
     */
    restHomeFullAddress() {
      const rh = this.restHome;
      const street = [rh.address, rh.num_address ? `nº ${rh.num_address}` : ""]
        .filter(Boolean)
        .join(", ");
      const district = [rh.neighborhood, rh.uf].filter(Boolean).join("/");
      const cep = this.restHomeCep ? `CEP: ${this.restHomeCep}` : "";

      const full = [street, district, cep].filter(Boolean).join(" – ");
      return full || "--";
    },

    /** "Cianorte, 16 de outubro de 2026." */
    placeAndDate() {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, "0");
      const month = MONTHS_PT[today.getMonth()];
      const year = today.getFullYear();
      const city = this.restHome.city ? `${this.restHome.city}, ` : "";
      return `${city}${day} de ${month} de ${year}.`;
    },

    /** Lista de campos obrigatórios vazios (aviso exibido só na tela) */
    missingFields() {
      if (!this.resident) return [];
      const rh = this.restHome;
      const checks = [
        [this.restHomeName, "Nome da instituição"],
        [rh.cnpj, "CNPJ da instituição"],
        [rh.address, "Endereço da instituição"],
        [rh.num_address, "Número do endereço da instituição"],
        [rh.city, "Cidade da instituição"],
        [this.resident.name, "Nome do residente"],
        [this.resident.rg, "RG do residente"],
        [this.resident.cpf, "CPF do residente"],
        [this.resident.date_of_birth, "Data de nascimento do residente"],
      ];
      return checks.filter(([value]) => !value).map(([, label]) => label);
    },
  },

  created() {
    this.retrieve();
  },

  methods: {
    formatDate,

    async retrieve() {
      const { id } = routes.history.current.params;
      this.id = id;

      // Lê o usuário UMA vez e normaliza os dados da instituição.
      // Assim os computed não chamam userData() a cada renderização.
      const { user } = userData();

      this.restHomeName = user.rest_home_name || user.rest_home_fantasy_name;
      this.restHomeLogoUrl = user.logo_url;

      this.restHome = {
        cnpj: user.rest_home_cnpj,
        address: user.rest_home_address,
        num_address: user.rest_home_num_address,
        neighborhood: user.rest_home_neighborhood,
        city: user.rest_home_city,
        // Sigla (PR), como na imagem de referência
        uf: user.rest_home_uf && user.rest_home_uf.initials,
        // Não existe no retorno atual; fica pronto caso o back-end inclua
        cep: user.rest_home_cep,
      };

      await this.get();
    },

    async get() {
      try {
        this.loading = true;

        // Reaproveita o endpoint de impressão já existente (dados do residente)
        const response = await api.get("/rest-home/resident/print", {
          params: { id: this.id },
        });
        const { error, errors, resident } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.resident = resident;
        }
      } catch (error) {
        console.log(error);
        this.errors = ["Não foi possível carregar os dados da declaração."];
      } finally {
        // finally garante que o overlay some mesmo em caso de erro
        this.loading = false;
      }
    },

    printOut() {
      window.print();
    },
  },
};
</script>

<style scoped>
/* =========================================================
   Tokens locais (não altera os CSS globais do projeto)
   ========================================================= */
.address-declaration {
  --ad-text: #1a1a1a;
  --ad-muted: #555;
  --ad-border: #333;
  --ad-primary: #0f65b0;
  --ad-primary-dark: #0a4d8a;
  --ad-alert-bg: #fff4e5;
  --ad-alert-border: #b45309;
  --ad-alert-text: #7c2d12;

  background: #f5f5f5;
  padding: 20px;
  min-height: 100vh;
}

.address-declaration__overlay {
  min-height: 100vh;
}

.address-declaration__container {
  max-width: 210mm; /* largura A4: o que se vê na tela = o que sai na folha */
  margin: 0 auto;
}

/* ===================== AÇÕES ===================== */
.address-declaration__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.address-declaration__alert {
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: var(--ad-alert-bg);
  border-left: 4px solid var(--ad-alert-border);
  border-radius: 4px;
  color: var(--ad-alert-text);
  font-size: 14px;
  line-height: 1.5;
}

.address-declaration__alert-icon {
  margin-top: 3px;
}

.address-declaration__alert-list {
  margin: 6px 0 0;
  padding-left: 20px;
}

.address-declaration__btn-print {
  background: var(--ad-primary) !important;
  color: #fff !important;
  min-height: 44px; /* alvo de toque mínimo (WCAG 2.5.5) */
  padding: 0 32px !important;
  border-radius: 8px;
  font-weight: 600;
  letter-spacing: 1px;
}

.address-declaration__btn-print:hover {
  background: var(--ad-primary-dark) !important;
}

.address-declaration__btn-print:focus-visible {
  outline: 3px solid var(--ad-primary-dark);
  outline-offset: 3px;
}

/* ===================== FOLHA ===================== */
.address-declaration__page {
  background: #fff;
  color: var(--ad-text);
  padding: 20mm 22mm;
  min-height: 297mm;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-family: Calibri, "Segoe UI", Arial, sans-serif;
  font-size: 12pt;
  line-height: 1.7;
}

.address-declaration__header {
  text-align: center;
  margin-bottom: 40px;
}

.address-declaration__logo {
  max-width: 200px;
  max-height: 110px;
  object-fit: contain;
}

.address-declaration__logo-fallback {
  font-size: 16pt;
  font-weight: 700;
  margin: 0;
}

.address-declaration__title {
  font-size: 12pt;
  font-weight: 700;
  margin: 0 0 12px;
  text-transform: uppercase;
}

.address-declaration__text {
  margin: 0 0 32px;
  text-align: justify;
}

/* Tabela sem bordas, como na referência */
.address-declaration__table {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 32px;
}

.address-declaration__table th,
.address-declaration__table td {
  text-align: left;
  vertical-align: bottom;
  padding: 6px 8px 6px 0;
}

.address-declaration__table th {
  font-weight: 700;
  padding-bottom: 14px;
}

.address-declaration__table th:first-child,
.address-declaration__table td:first-child {
  width: 40%;
}

/* Evita quebrar RG/CPF/data no meio do número */
.address-declaration__nowrap {
  white-space: nowrap;
}

.address-declaration__date {
  margin: 0 0 72px;
}

/* ===================== ASSINATURA ===================== */
.address-declaration__signature {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.address-declaration__signature-line {
  display: block;
  width: 320px;
  max-width: 100%;
  border-top: 1px solid var(--ad-border);
  margin-bottom: 6px;
}

.address-declaration__signature-name,
.address-declaration__signature-role {
  margin: 0;
  line-height: 1.4;
}

.address-declaration__signature-role {
  color: var(--ad-muted);
  font-size: 11pt;
}

/* Texto só para leitores de tela */
.address-declaration__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ===================== IMPRESSÃO ===================== */
@page {
  size: A4 portrait;
  margin: 0; /* a margem real fica no padding da folha */
}

@media print {
  .address-declaration {
    background: #fff !important;
    padding: 0 !important;
    min-height: auto;
  }

  .no-print {
    display: none !important;
  }

  .address-declaration__container {
    max-width: none;
  }

  .address-declaration__page {
    box-shadow: none !important;
    min-height: auto;
    page-break-inside: avoid;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}

/* ===================== TELAS PEQUENAS ===================== */
@media (max-width: 768px) {
  .address-declaration__page {
    padding: 24px 16px;
    min-height: auto;
    font-size: 11pt;
  }

  .address-declaration__table th:first-child,
  .address-declaration__table td:first-child {
    width: auto;
  }
}
</style>